// krida-ui-card-stack.js - Reusable Card Stack Element Creator

import { createCardElement } from './card-renderer.js';
import { getCardNumericValue } from './krida-patra-helpers.js';

/**
 * Creates a card stack element and calculates its visual width.
 * Relies on CSS classes (.card-stack, .card) for layout and overlap.
 * @param {Array} cards - Array of card objects.
 * @param {Object} config - Configuration { helpers: object with helper functions }.
 * @returns {Object|null} - { element: HTMLElement, width: number } or null if error.
 */
export function createStackElement(cards, config = {}) {
  if (!cards || cards.length === 0) return { element: null, width: 0 };
  if (!createCardElement) { console.error("createStackElement: createCardElement import failed?"); return null; }

  // Use helpers from config or fallback to direct import
  const helpers = config.helpers || { getCardNumericValue };
  
  // const { scale = 1.0 } = config; // No longer needed
  
  // --- Read sizing directly from CSS ---
  const styles = getComputedStyle(document.documentElement);
  let cardWidth, cardMargin;

  // Check the viewport width to decide which CSS variables to use
  if (window.innerWidth <= 768) {
      cardWidth = parseFloat(styles.getPropertyValue('--card-mobile-width'));
      // The margin is the overlap value
      cardMargin = parseFloat(styles.getPropertyValue('--card-mobile-overlap'));
  } else {
      cardWidth = parseFloat(styles.getPropertyValue('--card-desktop-width'));
      // The margin is the overlap value
      cardMargin = parseFloat(styles.getPropertyValue('--card-desktop-overlap'));
  }
  
  // --- Calculate Total Width using the Formula --- 
  let calculatedWidth = 0;
  if (cards.length > 0) {
      // The reveal amount is how much of the next card is visible.
      // Since margin is negative, adding it to the width gives the reveal.
      const reveal = cardWidth + cardMargin; 
      calculatedWidth = cardWidth + ((cards.length - 1) * reveal);
  } else {
      calculatedWidth = 0; // No cards, zero width
  }

  const stackWrapper = document.createElement('div');
  stackWrapper.className = 'card-stack'; 
  stackWrapper.style.setProperty('--card-count', cards.length);
  // No transform/scale should be applied here. Sizing is handled by CSS variables.

  // --- Create Card Elements --- 
  cards.forEach((card, cardIndex) => {
    if (card.numericValue === undefined && helpers && helpers.getCardNumericValue) {
      card.numericValue = helpers.getCardNumericValue(card);
    }
    const cardElement = createCardElement(card);
    cardElement.style.setProperty('--i', cardIndex); // Set index for CSS
    stackWrapper.appendChild(cardElement);
  });

  console.log(`createStackElement calculated width (unscaled): ${calculatedWidth} for ${cards.length} cards`);
  // Return the element and the *unscaled, calculated width*. The parent will use this.
  return { element: stackWrapper, width: calculatedWidth }; 
} 